import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Alert,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Paper,
  IconButton,
  useTheme,
  alpha,
  Fade,
  LinearProgress,
  TextField,
  MenuItem
} from '@mui/material';
import StoreIcon from '@mui/icons-material/Store';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import OnlineIcon from '@mui/icons-material/Wifi';
import OfflineIcon from '@mui/icons-material/WifiOff';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Pending from '@mui/icons-material/Pending';
import Payment from '@mui/icons-material/Payment';
import LinkOff from '@mui/icons-material/LinkOff';
import AccountBalanceWallet from '@mui/icons-material/AccountBalanceWallet';
import { toast } from 'react-toastify';
import { api } from '../services/AuthService';
import { escapeHtml } from '../utils/escapeHtml';
import { useAuth } from '../services/AuthService';
import { io } from 'socket.io-client';
import BrowserPrintService from '../services/BrowserPrintService';
import {
  bootstrapOrderPrintTracking,
  loadPersistedPrintedIds,
  persistPrintedId,
  removePersistedPrintedId,
  recentEligibleOrders,
  shouldAutoPrintOrder,
  shouldPrintOnOrderPoll,
  wasPrintedPersisted
} from '../utils/orderPrintRules';
import SubscriptionPaymentDialog from './SubscriptionPaymentDialog';
import CityAutocomplete from './CityAutocomplete';

const getSocketServerUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';

  try {
    const url = new URL(apiUrl);
    return url.origin;
  } catch (_) {
    return apiUrl.replace(/\/api\/?$/, '').replace(/\/$/, '');
  }
};

const getPrinterConfig = (value) => {
  if (value && typeof value === 'object') return value;
  try {
    return JSON.parse(value || '{}');
  } catch (_) {
    return {};
  }
};

const shouldPlayNewOrderSound = (pedido) => {
  const deliveryType = String(pedido?.tipo_entrega || '').trim().toUpperCase();
  const paymentMethod = String(pedido?.payment_method || '').trim().toLowerCase();
  // Mesa numerada, QR Code e Mesa Livre sao operacoes internas. Mesa Livre
  // pode nao possuir mesa_id, por isso waiter_id e payment_method tambem contam.
  return !pedido?.mesa_id
    && !pedido?.waiter_id
    && deliveryType !== 'MESA'
    && paymentMethod !== 'mesa';
};

function AdminLanchonete() {
  const { user } = useAuth();
  const theme = useTheme();
  const [lanchonete, setLanchonete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [pedidos, setPedidos] = useState([]);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [, setSocket] = useState(null);
  const [dayFilter, setDayFilter] = useState('todos'); // 'todos', 'hoje', 'ontem'
  const [dailyReport, setDailyReport] = useState(null);
  const [reportDate, setReportDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [promoMessage, setPromoMessage] = useState('');
  const [promoActive, setPromoActive] = useState(false);
  const [deliveryMode, setDeliveryMode] = useState('LOJA');
  const [storeLatitude, setStoreLatitude] = useState('');
  const [storeLongitude, setStoreLongitude] = useState('');
  const [savingStoreLocation, setSavingStoreLocation] = useState(false);
  const [storeCity, setStoreCity] = useState('');
  const [savingStoreCity, setSavingStoreCity] = useState(false);
  const [pixKey, setPixKey] = useState('');
  const [subscriptionPaymentOpen, setSubscriptionPaymentOpen] = useState(false);
  const [connectingMercadoPago, setConnectingMercadoPago] = useState(false);
  const [disconnectingMercadoPago, setDisconnectingMercadoPago] = useState(false);
  const [mercadoPagoStatus, setMercadoPagoStatus] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [waiterDialogOpen, setWaiterDialogOpen] = useState(false);
  const [waiterData, setWaiterData] = useState({ name: '', email: '', password: '' });
  const [pedidoSearch, setPedidoSearch] = useState('');
  const [pedidoOrigemFilter, setPedidoOrigemFilter] = useState('todos');
  const socketRef = useRef(null);
  const lanchoneteRef = useRef(null);
  const pedidosConhecidosRef = useRef(new Set());
  const pedidosImpressosRef = useRef(new Set());
  const pedidosCarregadosUmaVezRef = useRef(false);
  const pedidoStatusRef = useRef(new Map());
  const handleAutoPrintRef = useRef(null);
  const novoPedidoAudioRef = useRef(null);

  const playNovoPedidoAlert = useCallback(() => {
    const audio = novoPedidoAudioRef.current;
    if (!audio) return;

    audio.currentTime = 0;
    audio.play().catch((error) => {
      // Alguns navegadores exigem uma interacao na pagina antes de liberar audio.
      console.warn('Nao foi possivel reproduzir o alerta de novo pedido:', error);
    });
  }, []);

  const handleCreateWaiter = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users/waiter', waiterData);
      toast.success('Garçom criado com sucesso!');
      setWaiterDialogOpen(false);
      setWaiterData({ name: '', email: '', password: '' });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao criar garçom');
    }
  };

  const handleOpenMercadoPago = () => {
    if (!lanchonete?.id) return;
    setSubscriptionPaymentOpen(true);
  };

  const handleSubscriptionPaymentSuccess = async () => {
    await loadLanchoneteData();
  };

  const loadMercadoPagoStatus = useCallback(async () => {
    if (!user?.lanchonete_id) return;

    try {
      const response = await api.get(`/lanchonetes/${user.lanchonete_id}/mercadopago/status`);
      setMercadoPagoStatus(response.data);
    } catch (error) {
      console.error('Erro ao carregar status Mercado Pago:', error);
      setMercadoPagoStatus(null);
    }
  }, [user?.lanchonete_id]);

  const loadStripeStatus = useCallback(async () => {
    if (!user?.lanchonete_id) return;
    try {
      const response = await api.get(`/stripe-production/accounts/lanchonete/${user.lanchonete_id}/status`);
      setStripeStatus(response.data);
    } catch (error) {
      setStripeStatus({ connected: false, ready: false, unavailable: true });
    }
  }, [user?.lanchonete_id]);

  const handleConnectStripe = async () => {
    if (!lanchonete?.id) return;
    try {
      setConnectingStripe(true);
      const response = stripeStatus?.connected
        ? await api.post(`/stripe-production/accounts/lanchonete/${lanchonete.id}/onboarding-link`, { return_path: '/admin-lanchonete' })
        : await api.post('/stripe-production/accounts', { entity_type: 'lanchonete', entity_id: lanchonete.id, return_path: '/admin-lanchonete' });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao abrir cadastro Stripe');
      setConnectingStripe(false);
    }
  };

  const handleConnectMercadoPago = async () => {
    if (!lanchonete?.id) return;

    try {
      setConnectingMercadoPago(true);
      const response = await api.post(`/lanchonetes/${lanchonete.id}/mercadopago/connect-url`, {
        return_url: window.location.origin
      });
      const connectUrl = response.data?.url;

      if (!connectUrl) {
        toast.error('Mercado Pago nao retornou o link de conexao');
        return;
      }

      window.open(connectUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      const details = error.response?.data?.details;
      const message = typeof details === 'string'
        ? details
        : (details?.message || error.response?.data?.error || 'Erro ao conectar Mercado Pago');
      toast.error(message);
    } finally {
      setConnectingMercadoPago(false);
    }
  };

  const handleDisconnectMercadoPago = async () => {
    if (!lanchonete?.id) return;

    const confirmed = window.confirm(
      'Desconectar a conta Mercado Pago?\n\nPedidos com Pix e cartao deixarao de funcionar ate conectar outra conta.'
    );
    if (!confirmed) return;

    try {
      setDisconnectingMercadoPago(true);
      await api.post(`/lanchonetes/${lanchonete.id}/mercadopago/disconnect`);
      toast.success('Conta Mercado Pago desconectada');
      loadMercadoPagoStatus();
      loadLanchoneteData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao desconectar Mercado Pago');
    } finally {
      setDisconnectingMercadoPago(false);
    }
  };

  const initializeWebSocket = useCallback(() => {
    try {
      const token = localStorage.getItem('@DeliveryAdmin:token');
      if (!token) return;

      const newSocket = io(getSocketServerUrl(), {
        auth: {
          token: token
        }
      });

      newSocket.on('connect', () => {
        console.log('🔌 Conectado ao WebSocket');
        setSocket(newSocket);
        socketRef.current = newSocket;
      });

      newSocket.on('disconnect', () => {
        console.log('🔌 Desconectado do WebSocket');
        setSocket(null);
        socketRef.current = null;
      });

      newSocket.on('novo_pedido', async (data) => {
        console.log('📦 Novo pedido recebido:', data);

        // Adicionar pedido à lista
        const pedido = data?.data;
        if (!pedido?.id) return;

        setPedidos(prev => prev.some((item) => item.id === pedido.id) ? prev : [pedido, ...prev]);
        pedidosConhecidosRef.current.add(pedido.id);

        // Mesa Livre aparece e imprime normalmente, sem interromper a operacao.
        if (!pedido.suppressNotification) {
          toast.success(`📦 Novo Pedido #${pedido.id} recebido!`);
          if (shouldPlayNewOrderSound(pedido)) playNovoPedidoAlert();
        }

        // Imprimir automaticamente se habilitado
        if (lanchoneteRef.current?.auto_print) {
          await handleAutoPrintRef.current?.(pedido);
        }
      });

      newSocket.on('status_update', async (data) => {
        console.log('📋 Status atualizado:', data);
        setPedidos(prev => prev.map(p =>
          p.id === data.pedidoId ? { ...p, status: data.status } : p
        ));

        if (data?.pedidoId && data.status === 'pendente' && lanchoneteRef.current?.auto_print) {
          if (!pedidosImpressosRef.current.has(data.pedidoId)) {
            try {
              const response = await api.get(`/pedidos/${data.pedidoId}`);
              await handleAutoPrintRef.current?.(response.data);
            } catch (error) {
              console.error('Erro ao imprimir apos confirmacao de pagamento:', error);
            }
          }
        }
      });

      newSocket.on('connect_error', (error) => {
        console.error('❌ Erro na conexão WebSocket:', error);
      });

    } catch (error) {
      console.error('❌ Erro ao inicializar WebSocket:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playNovoPedidoAlert]);

  const handleAutoPrint = useCallback(async (pedido) => {
    const pedidoId = Number(pedido?.id);
    const lanchoneteId = lanchoneteRef.current?.id;
    if (!Number.isInteger(pedidoId) || pedidosImpressosRef.current.has(pedidoId)) return;
    if (wasPrintedPersisted(lanchoneteId, pedidoId)) {
      pedidosImpressosRef.current.add(pedidoId);
      return;
    }
    if (!lanchoneteRef.current?.auto_print) return;
    if (!shouldAutoPrintOrder(pedido)) return;

    try {
      // Reserva antes da impressao para impedir que polling, socket ou outra aba gerem segunda via.
      pedidosImpressosRef.current.add(pedidoId);
      persistPrintedId(lanchoneteId, pedidoId);
      const pedidoCompleto = pedido.items ? pedido : (await api.get(`/pedidos/${pedidoId}`)).data;
      const success = await BrowserPrintService.imprimirPedido(
        pedidoCompleto,
        lanchoneteRef.current,
        { automatic: true }
      );
      if (success) {
        toast.success(`Pedido #${pedidoId} enviado para impressao automatica`);
      } else {
        pedidosImpressosRef.current.delete(pedidoId);
        removePersistedPrintedId(lanchoneteId, pedidoId);
        toast.error('Falha na impressao automatica');
      }
    } catch (error) {
      pedidosImpressosRef.current.delete(pedidoId);
      removePersistedPrintedId(lanchoneteId, pedidoId);
      console.error('Erro na impressao automatica:', error);
      toast.error('Erro na impressao automatica');
    }
  }, []);

  handleAutoPrintRef.current = handleAutoPrint;

  const handlePrintPedido = async (pedido) => {
    try {
      const success = await BrowserPrintService.imprimirPedido(pedido, lanchonete);
      if (success) {
        toast.success('🖨️ Pedido impresso com sucesso');
      } else {
        toast.error('❌ Falha na impressão');
      }
    } catch (error) {
      console.error('Erro ao imprimir pedido:', error);
      toast.error('❌ Erro na impressão');
    }
  };

  const handlePrintTest = async () => {
    try {
      const success = await BrowserPrintService.imprimirTeste(lanchonete);
      if (success) {
        toast.success('🖨️ Teste de impressão realizado');
      } else {
        toast.error('❌ Falha no teste de impressão');
      }
    } catch (error) {
      console.error('Erro no teste de impressão:', error);
      toast.error('❌ Erro no teste de impressão');
    }
  };

  const loadLanchoneteData = useCallback(async () => {
    try {
      setLoading(true);

      // Verificar se o usuário tem lanchonete_id
      if (!user.lanchonete_id) {
        console.error('Usuário não tem lanchonete_id associado');
        toast.error('Você não está associado a nenhuma lanchonete');
        return;
      }

      const response = await api.get(`/lanchonetes/${user.lanchonete_id}`);
      setLanchonete(response.data);
      loadMercadoPagoStatus();

      // Carregar estatísticas gerais
      try {
        const statsRes = await api.get(`/lanchonetes/${user.lanchonete_id}/stats`);
        setStats(statsRes.data);
      } catch (err) {
        console.error('Erro ao carregar estatísticas:', err);
      }
    } catch (error) {
      console.error('Erro ao carregar dados da lanchonete:', error);
      toast.error('Erro ao carregar dados da lanchonete');
    } finally {
      setLoading(false);
    }
  }, [user.lanchonete_id, loadMercadoPagoStatus]);

  const filterPedidosByDay = (pedidos, filter) => {
    if (filter === 'todos') return pedidos;

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    return pedidos.filter(pedido => {
      const pedidoDate = new Date(pedido.created_at);

      if (filter === 'hoje') {
        return pedidoDate.toDateString() === today.toDateString();
      } else if (filter === 'ontem') {
        return pedidoDate.toDateString() === yesterday.toDateString();
      }

      return true;
    });
  };

  const isPedidoDeMesa = (pedido) =>
    Boolean(pedido.mesa_id) || String(pedido.tipo_entrega || '').toUpperCase() === 'MESA' || pedido.payment_method === 'mesa';

  const isPedidoDeAplicativo = (pedido) => !isPedidoDeMesa(pedido);

  const isPedidoHoje = (pedido) => {
    const pedidoDate = new Date(pedido.created_at);
    return pedidoDate.toDateString() === new Date().toDateString();
  };

  const pedidosFiltrados = pedidos.filter((pedido) => {
    const search = pedidoSearch.trim().toLowerCase();
    const matchesSearch =
      !search ||
      String(pedido.id).includes(search) ||
      String(pedido.customer_name || '').toLowerCase().includes(search) ||
      String(pedido.customer_phone || '').toLowerCase().includes(search);

    if (!matchesSearch) return false;
    if (pedidoOrigemFilter === 'mesa') return isPedidoDeMesa(pedido);
    if (pedidoOrigemFilter === 'aplicativo') return isPedidoDeAplicativo(pedido);
    return true;
  });

  const handlePrintPedidosHoje = async (origem) => {
    const pedidosParaImprimir = pedidos
      .filter(isPedidoHoje)
      .filter((pedido) => origem === 'mesa' ? isPedidoDeMesa(pedido) : isPedidoDeAplicativo(pedido));

    if (pedidosParaImprimir.length === 0) {
      toast.info(`Nenhum pedido de ${origem === 'mesa' ? 'mesa' : 'aplicativo'} encontrado hoje`);
      return;
    }

    const w = window.open('', '_blank');
    if (!w) {
      toast.error('Permita pop-ups para imprimir os pedidos');
      return;
    }

    try {
      const pedidosCompletos = await Promise.all(
        pedidosParaImprimir.map(async (pedido) => {
          const response = await api.get(`/pedidos/${pedido.id}`);
          return response.data;
        })
      );
      const cupons = pedidosCompletos
        .map((pedido) => BrowserPrintService.formatarCupom(pedido, lanchonete))
        .join('<div style="page-break-after: always;"></div>');

      w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Pedidos de hoje</title>
        <style>@media print{body{margin:0;padding:0;}@page{margin:5mm;}}</style>
        </head><body>${cupons}<script>window.onload=function(){window.print();}</script></body></html>`);
      w.document.close();
      toast.success('Pedidos enviados para impressão');
    } catch (error) {
      w.close();
      toast.error('Erro ao imprimir pedidos de hoje');
    }
  };

  const loadPedidos = useCallback(async () => {
    try {
      // Verificar se o usuário tem lanchonete_id
      if (!user.lanchonete_id) {
        console.error('Usuário não tem lanchonete_id associado');
        toast.error('Você não está associado a nenhuma lanchonete');
        return;
      }

      const response = await api.get(`/pedidos?lanchonete_id=${user.lanchonete_id}&limit=100`);
      const data = response.data;

      console.log('=== DEBUG ADMIN LANCHONETE ===');
      console.log('Pedidos recebidos:', data.length);
      if (data.length > 0) {
        console.log('Primeiro pedido:', {
          id: data[0].id,
          payment_method: data[0].payment_method,
          needs_change: data[0].needs_change,
          change_amount: data[0].change_amount
        });
      }

      // Verificar se pedidos são muito antigos (mais de 24 horas)
      const cutoffDate = new Date();
      cutoffDate.setHours(cutoffDate.getHours() - 24);

      const filteredByTime = data.filter(pedido => {
        const pedidoDate = new Date(pedido.created_at);
        return pedidoDate > cutoffDate || ['pendente', 'aceito', 'preparando', 'pronto', 'saiu_entrega'].includes(pedido.status);
      });

      // Aplicar filtro por dia
      const filteredByDay = filterPedidosByDay(filteredByTime, dayFilter);

      const printTracking = {
        knownIds: pedidosConhecidosRef.current,
        printedIds: pedidosImpressosRef.current,
        statusById: pedidoStatusRef.current
      };

      if (!pedidosCarregadosUmaVezRef.current) {
        loadPersistedPrintedIds(user.lanchonete_id).forEach((id) => pedidosImpressosRef.current.add(id));
        const recoverable = recentEligibleOrders(data, pedidosImpressosRef.current);
        bootstrapOrderPrintTracking(data, printTracking);
        recoverable.forEach((pedido) => pedidosImpressosRef.current.delete(pedido.id));
        pedidosCarregadosUmaVezRef.current = true;
        for (const pedido of recoverable) await handleAutoPrint(pedido);
      } else {
        for (const pedido of data) {
          if (shouldPrintOnOrderPoll(pedido, printTracking)) {
            await handleAutoPrint(pedido);
          }
        }
      }

      setPedidos(filteredByDay);
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
      toast.error('Erro ao carregar pedidos');
    }
  }, [user.lanchonete_id, dayFilter, handleAutoPrint]);

  const handleUpdatePedidoStatus = async (pedidoId, status) => {
    try {
      await api.patch(`/pedidos/${pedidoId}/status`, { status });
      setPedidos((prev) => prev.map((pedido) =>
        pedido.id === pedidoId ? { ...pedido, status } : pedido
      ));
      toast.success(status === 'pronto' ? 'Pedido marcado como pronto' : 'Status atualizado');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao atualizar status');
    }
  };

  const loadDailyReport = useCallback(async () => {
    if (!user?.lanchonete_id) return;
    try {
      const params = new URLSearchParams();
      if (reportDate) params.set('date', reportDate);
      const res = await api.get(`/pedidos/report/daily?${params.toString()}`);
      setDailyReport(res.data);
    } catch (error) {
      console.error('Erro ao carregar relatório diário:', error);
      toast.error('Erro ao carregar relatório do dia');
    }
  }, [user?.lanchonete_id, reportDate]);

  const handlePrintDailyReport = () => {
    if (!dailyReport || !lanchonete) return;
    const brl = (v) =>
      `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const w = window.open('', '_blank');
    if (!w) {
      toast.error('Permita pop-ups para imprimir o relatório');
      return;
    }
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Relatório</title>
      <style>
        * { color: #000000 !important; font-weight: 900 !important; box-sizing: border-box; }
        body { font-family: system-ui, sans-serif; padding: 5px; width: 240px; margin: 0 auto; }
        h1 { font-size: 14px; text-transform: uppercase; margin: 0 0 5px; }
        p { font-size: 11px; margin: 0 0 10px; }
        table { border-collapse: collapse; width: 100%; margin-top: 10px; }
        td, th { border: 2px solid #000; padding: 6px; text-align: left; font-size: 11px; }
        th { background: #fff; }
      </style>
      </head><body>
      <h1>Relatório — ${escapeHtml(lanchonete.name)}</h1>
      <p>Data: ${escapeHtml(reportDate)}</p>
      <table>
      <tr><th>Indicador</th><th>Valor</th></tr>
      <tr><td>Pedidos no dia</td><td>${dailyReport.total_pedidos ?? 0}</td></tr>
      <tr><td>Total vendas (dia)</td><td>${brl(dailyReport.total_valor)}</td></tr>
      <tr><td>Pedidos período dia (6h–18h)</td><td>${dailyReport.pedidos_dia_periodo ?? 0}</td></tr>
      <tr><td>Valor período dia</td><td>${brl(dailyReport.valor_dia_periodo)}</td></tr>
      <tr><td>Pedidos período noite (18h–6h)</td><td>${dailyReport.pedidos_noite ?? 0}</td></tr>
      <tr><td>Valor período noite</td><td>${brl(dailyReport.valor_noite)}</td></tr>
      </table>
      <script>window.onload=function(){window.print();}</script>
      </body></html>`;
    w.document.write(html);
    w.document.close();
  };

  const handleSavePromotion = async () => {
    if (!lanchonete?.id) return;
    try {
      setUpdating(true);
      await api.put(`/lanchonetes/${lanchonete.id}`, {
        promotion_active: promoActive,
        promotion_message: promoMessage,
        delivery_mode: deliveryMode,
        pix_key: pixKey
      });
      toast.success('Promoção salva');
      loadLanchoneteData();
    } catch (e) {
      toast.error('Erro ao salvar promoção');
    } finally {
      setUpdating(false);
    }
  };

  const handleCaptureStoreLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Este navegador não oferece acesso à localização. Informe latitude e longitude manualmente.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setStoreLatitude(String(Number(coords.latitude.toFixed(7))));
        setStoreLongitude(String(Number(coords.longitude.toFixed(7))));
        toast.success('Localização da lanchonete preenchida. Salve para aplicar.');
      },
      () => toast.error('Não foi possível obter a localização. Permita o GPS ou informe as coordenadas manualmente.'),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const handleSaveStoreLocation = async () => {
    if (!lanchonete?.id || !storeLatitude || !storeLongitude) {
      toast.error('Marque a localização ou informe latitude e longitude.');
      return;
    }
    try {
      setSavingStoreLocation(true);
      const { data } = await api.put(`/lanchonetes/${lanchonete.id}`, {
        latitude: Number(storeLatitude), longitude: Number(storeLongitude)
      });
      if (data?.lanchonete) setLanchonete(data.lanchonete);
      toast.success('Localização da lanchonete salva para o cálculo do frete.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Não foi possível salvar a localização.');
    } finally {
      setSavingStoreLocation(false);
    }
  };

  const handleSaveStoreCity = async () => {
    const city = String(storeCity || '').trim();
    if (!lanchonete?.id || !city) {
      toast.error('Selecione a cidade onde a lanchonete funciona.');
      return;
    }
    try {
      setSavingStoreCity(true);
      const { data } = await api.put(`/lanchonetes/${lanchonete.id}`, { city });
      if (data?.lanchonete) setLanchonete(data.lanchonete);
      toast.success('Cidade da lanchonete salva. Os motoboys desta cidade poderão receber as corridas.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Não foi possível salvar a cidade.');
    } finally {
      setSavingStoreCity(false);
    }
  };

  useEffect(() => {
    lanchoneteRef.current = lanchonete;
    if (lanchonete) {
      setPromoActive(Boolean(lanchonete.promotion_active));
      setPromoMessage(lanchonete.promotion_message || '');
      setDeliveryMode(String(lanchonete.delivery_mode || 'LOJA').toUpperCase() === 'APP' ? 'APP' : 'LOJA');
      setPixKey(lanchonete.pix_key || '');
      setStoreLatitude(lanchonete.latitude == null ? '' : String(lanchonete.latitude));
      setStoreLongitude(lanchonete.longitude == null ? '' : String(lanchonete.longitude));
      setStoreCity(lanchonete.city || '');
    }
  }, [lanchonete]);

  useEffect(() => {
    initializeWebSocket();
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
      }
    };
  }, [initializeWebSocket]);

  useEffect(() => {
    if (user?.lanchonete_id && lanchonete) {
      loadDailyReport();
    }
  }, [user?.lanchonete_id, lanchonete, loadDailyReport]);

  const handleToggleOnlineStatus = async (isOnline) => {
    try {
      setUpdating(true);
      await api.put(`/lanchonetes/${lanchonete.id}/online-status`, { is_online: isOnline });
      toast.success(`Lanchonete ${isOnline ? 'online' : 'offline'} com sucesso!`);
      loadLanchoneteData();
      loadPedidos();
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Erro ao alterar status online/offline');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdatePrinterConfig = async (config) => {
    try {
      setUpdating(true);
      await api.put(`/lanchonetes/${lanchonete.id}/printer-config`, config);
      setLanchonete((prev) => {
        const updated = prev ? { ...prev, ...config } : prev;
        lanchoneteRef.current = updated;
        return updated;
      });
      toast.success('Configuração de impressora atualizada com sucesso!');
      loadLanchoneteData();
    } catch (error) {
      toast.error('Erro ao atualizar configuração de impressora');
    } finally {
      setUpdating(false);
    }
  };

  const handleViewDetails = async (pedido) => {
    try {
      const response = await api.get(`/pedidos/${pedido.id}`);
      setSelectedPedido(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Erro ao buscar detalhes do pedido:', error);
      toast.error('Erro ao carregar detalhes do pedido');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente': return 'warning';
      case 'aceito': return 'info';
      case 'preparando': return 'secondary';
      case 'pronto': return 'warning';
      case 'saiu_entrega': return 'primary';
      case 'entregue': return 'success';
      case 'cancelado': return 'error';
      default: return 'default';
    }
  };


  const getStatusText = (status) => {
    switch (status) {
      case 'pendente': return 'Pendente';
      case 'aceito': return 'Aceito';
      case 'preparando': return 'Preparando';
      case 'pronto': return 'Pedido Pronto';
      case 'saiu_entrega': return 'Saiu para Entrega';
      case 'entregue': return 'Entregue';
      case 'cancelado': return 'Cancelado';
      default: return status;
    }
  };

  // Carregar dados na montagem do componente
  useEffect(() => {
    if (user) {
      loadLanchoneteData();
      loadPedidos();
      loadStripeStatus();
    }
  }, [user, loadLanchoneteData, loadPedidos, loadStripeStatus]);

  useEffect(() => {
    if (!user?.lanchonete_id) return undefined;

    const interval = setInterval(() => {
      loadPedidos();
    }, 30000);

    return () => clearInterval(interval);
  }, [user?.lanchonete_id, loadPedidos]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mp = params.get('mp');
    const message = params.get('message');
    const subscription = params.get('subscription');
    const stripeOnboarding = params.get('stripe_onboarding');

    if (stripeOnboarding) {
      toast.info(stripeOnboarding === 'complete' ? 'Cadastro Stripe recebido. Verificando aprovação...' : 'Continue o cadastro Stripe.');
      loadStripeStatus();
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    if (mp === 'connected') {
      toast.success('Conta Mercado Pago conectada com sucesso!');
      loadMercadoPagoStatus();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (mp === 'error') {
      toast.error(message || 'Nao foi possivel conectar a conta Mercado Pago');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (subscription === 'success') {
      toast.success('Pagamento recebido! Atualizando assinatura...');
      loadLanchoneteData();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (subscription === 'failure') {
      toast.error('Pagamento nao concluido no Mercado Pago');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (subscription === 'pending') {
      toast.info('Pagamento pendente de confirmacao no Mercado Pago');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [loadMercadoPagoStatus, loadLanchoneteData, loadStripeStatus]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          flexDirection: 'column',
          gap: 3
        }}
      >
        <Fade in={true}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress
              size={80}
              thickness={4}
              sx={{
                color: theme.palette.primary.contrastText,
                mb: 2
              }}
            />
            <Typography variant="h6" color="inherit">
              Carregando painel...
            </Typography>
            <Typography variant="body2" color="inherit" sx={{ opacity: 0.8 }}>
              Preparando suas informações
            </Typography>
          </Box>
        </Fade>
        <LinearProgress
          sx={{
            width: '200px',
            bgcolor: alpha(theme.palette.primary.contrastText, 0.2),
            '& .MuiLinearProgress-bar': {
              bgcolor: theme.palette.primary.contrastText
            }
          }}
        />
      </Box>
    );
  }

  if (!lanchonete) {
    return (
      <Alert severity="warning">
        Você não está associado a nenhuma lanchonete. Entre em contato com o administrador.
      </Alert>
    );
  }

  const isOpen = lanchonete.is_open;
  const isOnline = lanchonete.is_online;
  const subscriptionInfo = lanchonete.subscription_enforcement;

  const getSubscriptionStatusLabel = () => {
    switch (subscriptionInfo?.phase) {
      case 'trial':
        return 'TESTE GRATUITO';
      case 'active':
        return 'ATIVA';
      case 'grace':
        return 'PAGAMENTO PENDENTE';
      case 'overdue':
        return 'ATRASADA';
      case 'blocked':
        return 'BLOQUEADA';
      default:
        if (lanchonete.subscription_status === 'active') return 'ATIVA';
        if (lanchonete.subscription_status === 'trialing') return 'TESTE GRATUITO';
        return 'PAGAMENTO PENDENTE';
    }
  };

  const getSubscriptionStatusColor = () => {
    const phase = subscriptionInfo?.phase;
    if (phase === 'active' || phase === 'trial') return 'success.main';
    if (phase === 'grace') return 'warning.main';
    return 'error.main';
  };

  const formatSubscriptionDate = (value) => {
    if (!value) return null;
    return new Date(value).toLocaleDateString('pt-BR');
  };

  return (
    <Box>
      <audio ref={novoPedidoAudioRef} src="/novo-pedido.mp3" preload="auto" />
      {subscriptionInfo && subscriptionInfo.phase !== 'active' && subscriptionInfo.phase !== 'trial' && (
        <Alert
          severity={subscriptionInfo.phase === 'grace' ? 'warning' : 'error'}
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            {subscriptionInfo.phase === 'grace'
              ? 'Mensalidade pendente - periodo de carencia'
              : 'Conta com restricao por mensalidade'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {subscriptionInfo.message}
          </Typography>
          {subscriptionInfo.graceUntil && subscriptionInfo.phase === 'grace' && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Carencia ate: {formatSubscriptionDate(subscriptionInfo.graceUntil)}
            </Typography>
          )}
          {subscriptionInfo.dueDate && (
            <Typography variant="caption" display="block">
              Vencimento: {formatSubscriptionDate(subscriptionInfo.dueDate)}
            </Typography>
          )}
        </Alert>
      )}

      {subscriptionInfo && (subscriptionInfo.phase === 'active' || subscriptionInfo.phase === 'trial') && subscriptionInfo.dueDate && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Plano ativo. Apos o vencimento ha {subscriptionInfo.graceDays || 3} dias de carencia antes de bloquear novos pedidos.
          {formatSubscriptionDate(subscriptionInfo.dueDate) && (
            <> Proximo vencimento: {formatSubscriptionDate(subscriptionInfo.dueDate)}.</>
          )}
        </Alert>
      )}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Minha Lanchonete
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PersonAddIcon />}
            onClick={() => setWaiterDialogOpen(true)}
          >
            Novo Garçom
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => {
              const fromOnboarding = new URLSearchParams(window.location.search).get('from') === 'onboarding';
              window.location.href = `/admin/lanchonetes?edit=1${fromOnboarding ? '&from=onboarding' : ''}`;
            }}
          >
            Editar Dados
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>Cidade da lanchonete</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Informe a cidade onde o estabelecimento funciona para combinar com a cidade escolhida pelos motoboys.
          </Typography>
          <Box display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
            <CityAutocomplete
              value={storeCity}
              onChange={setStoreCity}
              sx={{ flex: '1 1 320px' }}
              textFieldProps={{ fullWidth: true, label: 'Cidade e estado', required: true, helperText: 'Exemplo: Carnaíba - PE' }}
            />
            <Button variant="contained" onClick={handleSaveStoreCity} disabled={savingStoreCity || !storeCity.trim()}>
              {savingStoreCity ? 'Salvando...' : 'Salvar cidade'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>Localização para o frete por KM</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Marque o GPS enquanto estiver na lanchonete. O app usa este ponto e a localização exata do cliente para estimar a distância do frete.
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}><TextField fullWidth label="Latitude" value={storeLatitude} onChange={(event) => setStoreLatitude(event.target.value)} inputProps={{ inputMode: 'decimal' }} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Longitude" value={storeLongitude} onChange={(event) => setStoreLongitude(event.target.value)} inputProps={{ inputMode: 'decimal' }} /></Grid>
            <Grid item xs={12} sm={4}>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button variant="outlined" onClick={handleCaptureStoreLocation}>Usar GPS daqui</Button>
                <Button variant="contained" onClick={handleSaveStoreLocation} disabled={savingStoreLocation || !storeLatitude || !storeLongitude}>{savingStoreLocation ? 'Salvando...' : 'Salvar localização'}</Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Card Principal */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={3}>
                <StoreIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    {lanchonete.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {lanchonete.description}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Status em Tempo Real */}
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Status em Tempo Real
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Card
                      sx={{
                        bgcolor: isOpen ? 'success.light' : 'error.light',
                        border: isOpen ? '2px solid' : '2px solid',
                        borderColor: isOpen ? 'success.main' : 'error.main'
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="h6" color="white" fontWeight="bold">
                          {isOpen ? 'ABERTO' : 'FECHADO'}
                        </Typography>
                        <Typography variant="body2" color="white">
                          {lanchonete.status_text}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Card
                      sx={{
                        bgcolor: isOnline ? 'info.light' : 'grey.300',
                        border: isOnline ? '2px solid' : '2px solid',
                        borderColor: isOnline ? 'info.main' : 'grey.500'
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="h6" color="white" fontWeight="bold">
                          {isOnline ? 'ONLINE' : 'OFFLINE'}
                        </Typography>
                        <Typography variant="body2" color="white">
                          {isOnline ? 'Aceitando pedidos' : 'Pedidos pausados'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>

              {/* Mensalidade da plataforma */}
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Mensalidade da Plataforma
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: alpha(theme.palette.warning.main, 0.04) }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={8}>
                      <Typography variant="body2" color="textSecondary">Status da assinatura:</Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          color={getSubscriptionStatusColor()}
                        >
                          {getSubscriptionStatusLabel()}
                        </Typography>
                        {(['active', 'trial'].includes(subscriptionInfo?.phase) ||
                          ['active', 'trialing'].includes(lanchonete.subscription_status)) ? (
                          <CheckCircle color="success" />
                        ) : (
                          <Pending color={subscriptionInfo?.phase === 'grace' ? 'warning' : 'error'} />
                        )}
                      </Box>
                      {lanchonete.trial_end_date && (subscriptionInfo?.phase === 'trial' || lanchonete.subscription_status === 'trialing') && (
                        <Typography variant="caption" display="block">
                          Teste gratuito ate: {new Date(lanchonete.trial_end_date).toLocaleDateString()}
                        </Typography>
                      )}
                      {(lanchonete.subscription_due_date || subscriptionInfo?.dueDate) && (
                        <Typography variant="caption" display="block">
                          {(['active', 'trial'].includes(subscriptionInfo?.phase) ||
                            ['active', 'trialing', 'trial'].includes(lanchonete.subscription_status))
                            ? 'Proximo pagamento: '
                            : 'Vencimento: '}
                          {formatSubscriptionDate(subscriptionInfo?.dueDate || lanchonete.subscription_due_date)}
                        </Typography>
                      )}
                      {subscriptionInfo?.graceUntil && subscriptionInfo.phase === 'grace' && (
                        <Typography variant="caption" display="block" color="warning.main" fontWeight="bold">
                          Carencia ate: {formatSubscriptionDate(subscriptionInfo.graceUntil)}
                        </Typography>
                      )}
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        Regras: {subscriptionInfo?.graceDays || 3} dias de carencia apos vencimento, depois bloqueia pedidos.
                        Apos {subscriptionInfo?.blockAdminAfterDays || 7} dias limita o painel.
                        Apos {subscriptionInfo?.blockTotalAfterDays || 10} dias bloqueia tudo.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button
                        variant="contained"
                        color="warning"
                        fullWidth
                        startIcon={<Payment />}
                        onClick={handleOpenMercadoPago}
                      >
                        Pagar mensalidade
                      </Button>
                      <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                        Escolha Pix ou cartao. Apos o pagamento, o proximo vencimento e atualizado automaticamente.
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              {/* Conta Mercado Pago para receber pedidos */}
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Recebimento de Pedidos (Mercado Pago)
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.04) }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={8}>
                      <Typography variant="body2" color="textSecondary">
                        Conta vinculada para Pix e cartao dos clientes:
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight="bold"
                          color={mercadoPagoStatus?.connected ? 'success.main' : 'warning.main'}
                        >
                          {mercadoPagoStatus?.connected ? 'CONTA CONECTADA' : 'NENHUMA CONTA CONECTADA'}
                        </Typography>
                        {mercadoPagoStatus?.connected ? <CheckCircle color="success" /> : <Pending color="warning" />}
                      </Box>
                      {mercadoPagoStatus?.mp_user_id && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Conta MP: {mercadoPagoStatus.mp_user_id}
                        </Typography>
                      )}
                      {mercadoPagoStatus?.connected_at && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Conectada em: {new Date(mercadoPagoStatus.connected_at).toLocaleString('pt-BR')}
                        </Typography>
                      )}
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                        Esta conta e diferente da mensalidade. Aqui voce recebe os pagamentos dos pedidos do app.
                      </Typography>
                      <Alert severity="info" sx={{ mt: 1.5 }}>
                        Nos pagamentos online pelo Mercado Pago, sera descontada uma comissao de{' '}
                        <strong>{Number(mercadoPagoStatus?.sales_fee_percent ?? 2).toLocaleString('pt-BR')}%</strong>{' '}
                        sobre o valor dos produtos. Nas entregas feitas pelo APP, a taxa de entrega da plataforma
                        tambem e separada. O valor total cobrado do cliente nao aumenta por causa dessa comissao.
                      </Alert>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button
                        variant={mercadoPagoStatus?.connected ? 'outlined' : 'contained'}
                        color={mercadoPagoStatus?.connected ? 'success' : 'primary'}
                        fullWidth
                        startIcon={<AccountBalanceWallet />}
                        onClick={handleConnectMercadoPago}
                        disabled={connectingMercadoPago || disconnectingMercadoPago}
                        sx={{ mb: 1 }}
                      >
                        {connectingMercadoPago
                          ? 'Abrindo...'
                          : (mercadoPagoStatus?.connected ? 'Trocar conta' : 'Conectar conta')}
                      </Button>
                      {mercadoPagoStatus?.connected && (
                        <Button
                          variant="outlined"
                          color="error"
                          fullWidth
                          startIcon={<LinkOff />}
                          onClick={handleDisconnectMercadoPago}
                          disabled={disconnectingMercadoPago || connectingMercadoPago}
                        >
                          {disconnectingMercadoPago ? 'Desconectando...' : 'Desconectar conta'}
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              {/* Cadastro Stripe preparado sem desligar o Mercado Pago */}
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Preparar recebimento pela Stripe
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={8}>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Chip
                          color={stripeStatus?.ready ? 'success' : 'warning'}
                          label={stripeStatus?.ready ? 'Stripe pronta' : stripeStatus?.connected ? 'Cadastro pendente' : 'Stripe não conectada'}
                        />
                        <Chip
                          variant="outlined"
                          label={(lanchonete.payment_provider || 'mercadopago') === 'stripe' ? 'Novos pedidos: Stripe' : 'Novos pedidos: Mercado Pago'}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Você pode concluir o cadastro bancário agora. O Mercado Pago continua funcionando até o Admin Master mudar esta lanchonete para Stripe.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      {!stripeStatus?.ready && (
                        <Button fullWidth variant="contained" disabled={connectingStripe || stripeStatus?.unavailable} onClick={handleConnectStripe}>
                          {connectingStripe ? 'Abrindo...' : stripeStatus?.connected ? 'Continuar cadastro Stripe' : 'Conectar à Stripe'}
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              {/* Estatísticas Gerais */}
              {stats && (
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom>
                    Resumo de Pedidos
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                        <Typography variant="caption" color="textSecondary" display="block">Total</Typography>
                        <Typography variant="h6" fontWeight="bold">{stats.total_pedidos}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                        <Typography variant="caption" color="success.main" display="block">Entregues</Typography>
                        <Typography variant="h6" fontWeight="bold" color="success.main">{stats.pedidos_entregues}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
                        <Typography variant="caption" color="warning.main" display="block">Pendentes</Typography>
                        <Typography variant="h6" fontWeight="bold" color="warning.main">{stats.pedidos_pendentes}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                        <Typography variant="caption" color="info.main" display="block">Em Rota</Typography>
                        <Typography variant="h6" fontWeight="bold" color="info.main">{stats.pedidos_entregando}</Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Controle Online/Offline */}
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Controle de Pedidos
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(isOnline)}
                      onChange={(e) => handleToggleOnlineStatus(e.target.checked)}
                      color="success"
                      disabled={updating || (subscriptionInfo && !subscriptionInfo.canGoOnline && !isOnline)}
                      size="large"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        Lanchonete {isOnline ? 'Online' : 'Offline'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {subscriptionInfo && !subscriptionInfo.canGoOnline
                          ? 'Indisponivel por pendencia de mensalidade. Pague para voltar a aceitar pedidos.'
                          : (isOnline
                            ? 'Sua lanchonete esta aceitando pedidos'
                            : 'Sua lanchonete nao esta aceitando pedidos')}
                      </Typography>
                    </Box>
                  }
                />
                {updating && (
                  <Box display="flex" alignItems="center" mt={1}>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    <Typography variant="body2" color="textSecondary">
                      Atualizando...
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Informações de Contato */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Informações de Contato
                </Typography>
                <Grid container spacing={2}>
                  {lanchonete.phone && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center">
                        <PhoneIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2">{lanchonete.phone}</Typography>
                      </Box>
                    </Grid>
                  )}
                  {lanchonete.email && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center">
                        <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2">{lanchonete.email}</Typography>
                      </Box>
                    </Grid>
                  )}
                  {lanchonete.address && (
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center">
                        <LocationOnIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2">{lanchonete.address}</Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Card de Configuração de Impressora */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <PrintIcon sx={{ fontSize: 24, mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Configuração de Impressora
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Toggle Auto Print */}
              <Box mb={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={lanchonete.auto_print || false}
                      onChange={(e) => handleUpdatePrinterConfig({ auto_print: e.target.checked })}
                      disabled={updating}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        Impressão Automática
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Imprimir pedidos automaticamente quando recebidos
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              <Box mb={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={getPrinterConfig(lanchonete.printer_config).waiter_kitchen_only === true}
                      onChange={(e) => handleUpdatePrinterConfig({
                        printer_config: {
                          ...getPrinterConfig(lanchonete.printer_config),
                          waiter_kitchen_only: e.target.checked
                        }
                      })}
                      disabled={updating}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        Garcom: imprimir somente itens da cozinha
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Bebidas continuam na mesa e na conta, mas nao gastam papel na impressao automatica.
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              {/* Status da Conexão */}
              <Box mb={3}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Status da Conexão:
                </Typography>
                <Chip
                  label={socketRef.current ? "Conectado" : "Desconectado"}
                  color={socketRef.current ? "success" : "error"}
                  size="small"
                  icon={socketRef.current ? <OnlineIcon /> : <OfflineIcon />}
                />
              </Box>

              {/* Botões de Ação */}
              <Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PrintIcon />}
                  onClick={handlePrintTest}
                  disabled={updating || !lanchonete}
                  sx={{ mr: 1, mb: 1 }}
                >
                  Testar Navegador
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PrintIcon />}
                  onClick={() => selectedPedido && handlePrintPedido(selectedPedido)}
                  disabled={updating || !selectedPedido}
                  sx={{ mb: 1 }}
                >
                  Imprimir Pedido
                </Button>
              </Box>

              {/* Informações de Suporte */}
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  A impressao automatica usa a impressora configurada neste computador/navegador. Deixe esta tela aberta para receber e imprimir novos pedidos.
                </Typography>
              </Alert>
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Para a impressao automatica funcionar
                </Typography>
                <Typography variant="caption" component="div" sx={{ lineHeight: 1.6 }}>
                  1. Baixe e instale o aplicativo All Delivery neste computador.<br />
                  2. Abra o atalho &quot;All Delivery - Gestao de Pedidos&quot; criado na Area de Trabalho.<br />
                  3. Defina a impressora correta como padrao no Windows e deixe o aplicativo aberto.
                </Typography>
                <Button
                  component="a"
                  href="/downloads/All-Delivery-Setup-1.0.4-x64.exe"
                  download
                  size="small"
                  variant="contained"
                  sx={{ mt: 1.5 }}
                >
                  Windows 10/11 (64 bits)
                </Button>
                <Button
                  component="a"
                  href="/downloads/All-Delivery-Setup-1.0.4-win7-ia32.exe"
                  download
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1, ml: 1 }}
                >
                  Windows 7 / 32 bits (legado)
                </Button>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Card de Horários */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Horário de Funcionamento
              </Typography>
              <Box display="flex" alignItems="center" mb={2}>
                <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body2">
                  {lanchonete.opening_hours || 'Não definido'}
                </Typography>
              </Box>

              {lanchonete.opening_time && lanchonete.closing_time && (
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Horário Específico:
                  </Typography>
                  <Chip
                    label={`Abertura: ${lanchonete.opening_time}`}
                    size="small"
                    color="primary"
                    sx={{ mb: 1 }}
                  />
                  <br />
                  <Chip
                    label={`Fechamento: ${lanchonete.closing_time}`}
                    size="small"
                    color="secondary"
                  />
                </Box>
              )}

              <Box mt={2}>
                <Typography variant="body2" color="textSecondary">
                  Taxa de Entrega:
                </Typography>
                <Typography variant="h6" color="primary.main">
                  {lanchonete.delivery_fee > 0
                    ? `R$ ${parseFloat(lanchonete.delivery_fee || 0).toFixed(2)}`
                    : 'Grátis'
                  }
                </Typography>
              </Box>

              {lanchonete.min_order_value > 0 && (
                <Box mt={2}>
                  <Typography variant="body2" color="textSecondary">
                    Pedido Mínimo:
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    R$ {parseFloat(lanchonete.min_order_value || 0).toFixed(2)}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Configurações da Loja
              </Typography>

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Promoção no app
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Ao ativar, clientes que favoritaram a loja recebem uma notificação no app com a mensagem abaixo.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={promoActive}
                    onChange={(e) => setPromoActive(e.target.checked)}
                    disabled={updating}
                  />
                }
                label={promoActive ? 'Promoção ativa' : 'Promoção desligada'}
              />
              <TextField
                fullWidth
                multiline
                minRows={2}
                sx={{ mt: 1, mb: 2 }}
                label="Mensagem da promoção"
                value={promoMessage}
                onChange={(e) => setPromoMessage(e.target.value)}
                placeholder="Ex.: 20% em pizzas hoje à noite"
              />

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                Pagamento e Entrega
              </Typography>

              <TextField
                fullWidth
                select
                SelectProps={{ native: true }}
                sx={{ mb: 2 }}
                label="Tipo de entrega da loja"
                value={deliveryMode}
                onChange={(e) => setDeliveryMode(e.target.value)}
              >
                <option value="LOJA">Motoboy da lanchonete (LOJA)</option>
                <option value="APP">Motoboy do aplicativo (APP)</option>
              </TextField>

              <TextField
                fullWidth
                label="Chave PIX para Recebimento"
                value={pixKey}
                onChange={(e) => setPixKey(e.target.value)}
                placeholder="CPF, CNPJ, E-mail ou Aleatória"
                helperText="Chave mostrada no app para pagamentos via PIX Manual"
                sx={{ mb: 2 }}
              />

              <Button
                variant="contained"
                fullWidth
                onClick={handleSavePromotion}
                disabled={updating}
                startIcon={updating ? <CircularProgress size={20} color="inherit" /> : null}
              >
                Salvar Configurações
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} flexDirection={{ xs: 'column', sm: 'row' }} gap={2} mb={2}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Relatório do dia
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Totais e comparativo período dia (6h–18h) × noite (18h–6h), no fuso do servidor.
                  </Typography>
                </Box>
                <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                  <TextField
                    type="date"
                    size="small"
                    label="Data"
                    InputLabelProps={{ shrink: true }}
                    value={reportDate}
                    onChange={(e) => setReportDate(e.target.value)}
                  />
                  <Button variant="outlined" size="small" onClick={() => loadDailyReport()}>
                    Atualizar
                  </Button>
                  <Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={handlePrintDailyReport}>
                    Imprimir
                  </Button>
                </Box>
              </Box>
              {dailyReport ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">Pedidos (dia)</Typography>
                      <Typography variant="h5">{dailyReport.total_pedidos ?? 0}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">Valor total</Typography>
                      <Typography variant="h5">
                        R$ {Number(dailyReport.total_valor ?? 0).toFixed(2)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">Noite (pedidos / valor)</Typography>
                      <Typography variant="h6">
                        {dailyReport.pedidos_noite ?? 0} — R$ {Number(dailyReport.valor_noite ?? 0).toFixed(2)}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">Dia 6h–18h (pedidos / valor)</Typography>
                      <Typography variant="h6">
                        {dailyReport.pedidos_dia_periodo ?? 0} — R$ {Number(dailyReport.valor_dia_periodo ?? 0).toFixed(2)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">Carregando relatório…</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Card de Pedidos Recentes */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" gutterBottom>
                  Pedidos Recentes
                </Typography>

                {/* Filtros por Dia */}
                <Box display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PrintIcon />}
                    onClick={() => handlePrintPedidosHoje('aplicativo')}
                  >
                    Imprimir app hoje
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PrintIcon />}
                    onClick={() => handlePrintPedidosHoje('mesa')}
                  >
                    Imprimir mesas hoje
                  </Button>
                  <Button
                    variant={dayFilter === 'todos' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => {
                      setDayFilter('todos');
                      loadPedidos();
                    }}
                  >
                    Todos
                  </Button>
                  <Button
                    variant={dayFilter === 'hoje' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => {
                      setDayFilter('hoje');
                      loadPedidos();
                    }}
                  >
                    Hoje
                  </Button>
                  <Button
                    variant={dayFilter === 'ontem' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => {
                      setDayFilter('ontem');
                      loadPedidos();
                    }}
                  >
                    Ontem
                  </Button>
                </Box>
              </Box>
              <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
                <TextField
                  size="small"
                  label="Pesquisar pedido"
                  value={pedidoSearch}
                  onChange={(e) => setPedidoSearch(e.target.value)}
                  placeholder="ID, cliente ou telefone"
                  sx={{ minWidth: 240 }}
                />
                <TextField
                  select
                  size="small"
                  label="Origem"
                  value={pedidoOrigemFilter}
                  onChange={(e) => setPedidoOrigemFilter(e.target.value)}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="aplicativo">Aplicativo</MenuItem>
                  <MenuItem value="mesa">Mesas</MenuItem>
                </TextField>
              </Box>
              {pedidosFiltrados.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  Nenhum pedido encontrado
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Cliente</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell>Pagamento</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Data</TableCell>
                        <TableCell align="right">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pedidosFiltrados.map((pedido) => (
                        <TableRow key={pedido.id}>
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight="bold">
                              #{pedido.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {pedido.customer_name || 'Não informado'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {pedido.customer_phone}
                            </Typography>
                          </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Pedido
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            R$ {parseFloat((pedido.total_amount || 0) - (pedido.delivery_fee || 0)).toFixed(2)}
                          </Typography>
                          {Number(pedido.delivery_fee || 0) > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Taxa separada: R$ {parseFloat(pedido.delivery_fee || 0).toFixed(2)}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" display="block">
                            Total: R$ {parseFloat(pedido.total_amount || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{
                              fontWeight: 'bold',
                              color: pedido.payment_method === 'pix' ? 'success.main' : 'text.primary'
                            }}>
                              {pedido.payment_method === 'pix' ? '📱 Pix' :
                               pedido.payment_method === 'money' ? '💰 Dinheiro' :
                               pedido.payment_method === 'delivery' ? '🏠 Pagar na Entrega' :
                               pedido.payment_method === 'pickup' ? '🏃 Retirar no Local' :
                               pedido.payment_method === 'local' ? '🍽️ Comer no Local' :
                               '💰 Não informado'}
                            </Typography>
                            {(pedido.needs_change === 1 || pedido.needs_change === true) && pedido.change_amount > 0 && (
                              <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold' }}>
                                💸 Troco: R$ {parseFloat(pedido.change_amount || 0).toFixed(2)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusText(pedido.status)}
                              color={getStatusColor(pedido.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {new Date(pedido.created_at).toLocaleString('pt-BR')}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Box display="flex" gap={1} justifyContent="flex-end">
                              <Button
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => handleViewDetails(pedido)}
                              >
                                Ver
                              </Button>
                              {pedido.status === 'pendente' && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleUpdatePedidoStatus(pedido.id, 'aceito')}
                                >
                                  Aceitar
                                </Button>
                              )}
                              {['aceito', 'preparando'].includes(pedido.status) && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  onClick={() => handleUpdatePedidoStatus(pedido.id, 'pronto')}
                                >
                                  Pronto
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog de detalhes do pedido */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: theme.shadows[8]
          }
        }}
      >
        <DialogTitle sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: 3,
          px: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              width: 56,
              height: 56
            }}>
              <ReceiptIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Pedido #{selectedPedido?.id}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {new Date(selectedPedido?.created_at).toLocaleString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setDetailDialogOpen(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {selectedPedido && (
            <Grid container>
              {/* Informações do Cliente */}
              <Grid item xs={12} md={4}>
                <Paper sx={{
                  p: 3,
                  m: 2,
                  background: 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2
                }}>
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                      <PersonIcon />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold">
                      Dados do Cliente
                    </Typography>
                  </Box>

                  <Box sx={{ '& > *': { mb: 2 } }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Nome
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedPedido.customer_name || 'Não informado'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Telefone
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedPedido.customer_phone}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Endereço de Entrega
                      </Typography>
                      <Typography variant="body2" sx={{
                        backgroundColor: 'rgba(0,0,0,0.05)',
                        p: 1.5,
                        borderRadius: 1,
                        border: `1px dashed ${theme.palette.divider}`
                      }}>
                        {selectedPedido.delivery_address}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Referência
                      </Typography>
                      <Typography variant="body2" sx={{
                        backgroundColor: 'rgba(0,0,0,0.05)',
                        p: 1.5,
                        borderRadius: 1,
                        border: `1px dashed ${theme.palette.divider}`
                      }}>
                        {selectedPedido.reference || 'Não informada'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Forma de Pagamento
                      </Typography>
                      <Typography variant="body2" sx={{
                        backgroundColor: 'rgba(0,0,0,0.05)',
                        p: 1.5,
                        borderRadius: 1,
                        border: `1px dashed ${theme.palette.divider}`,
                        fontWeight: 'bold',
                        color: selectedPedido.payment_method === 'money' ? 'success.main' : 'text.primary'
                      }}>
                        {selectedPedido.payment_method === 'pix' ? '📱 Pix' :
                         selectedPedido.payment_method === 'money' ? '💰 Dinheiro' :
                         selectedPedido.payment_method === 'delivery' ? '🏠 Pagar na Entrega' :
                         selectedPedido.payment_method === 'pickup' ? '🏃 Retirar no Local' :
                         selectedPedido.payment_method === 'local' ? '🍽️ Comer no Local' :
                         '💰 Não informado'}
                      </Typography>
                    </Box>

                    {selectedPedido.needs_change && selectedPedido.change_amount > 0 && (
                      <Box>
                        <Typography variant="caption" color="textSecondary" display="block">
                          💸 Troco Necessário
                        </Typography>
                        <Typography variant="body2" sx={{
                          backgroundColor: 'success.light',
                          p: 1.5,
                          borderRadius: 1,
                          border: `2px solid ${theme.palette.success.main}`,
                          fontWeight: 'bold',
                          color: 'success.dark'
                        }}>
                          R$ {parseFloat(selectedPedido.change_amount || 0).toFixed(2)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Itens do Pedido */}
              <Grid item xs={12} md={8}>
                <Box sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                      <ShoppingCartIcon />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold">
                      Itens do Pedido
                    </Typography>
                    <Chip
                      label={getStatusText(selectedPedido.status)}
                      color={getStatusColor(selectedPedido.status)}
                      sx={{ ml: 'auto' }}
                    />
                  </Box>

                  <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                    {selectedPedido.itens?.map((item, index) => (
                      <Card key={index} sx={{
                        mb: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        '&:hover': {
                          boxShadow: theme.shadows[4],
                          transform: 'translateY(-2px)',
                          transition: 'all 0.3s ease'
                        }
                      }}>
                        <CardContent sx={{ p: 2 }}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={2}>
                              <Avatar
                                src={item.produto_image}
                                sx={{
                                  width: 60,
                                  height: 60,
                                  bgcolor: theme.palette.grey[200],
                                  border: `2px solid ${theme.palette.divider}`
                                }}
                              >
                                <ShoppingCartIcon />
                              </Avatar>
                            </Grid>
                            <Grid item xs={10}>
                              <Box display="flex" justifyContent="space-between" alignItems="start">
                                <Box flex={1}>
                                  <Typography variant="h6" fontWeight="bold" color="primary">
                                    {item.quantity}x {item.produto_name}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    R$ {parseFloat(item.unit_price || 0).toFixed(2)} unidade
                                  </Typography>
                                  {item.observation && (
                                    <Box sx={{
                                      mt: 1,
                                      p: 1,
                                      backgroundColor: theme.palette.info.light,
                                      borderRadius: 1,
                                      borderLeft: `4px solid ${theme.palette.info.main}`
                                    }}>
                                      <Typography variant="caption" sx={{
                                        color: theme.palette.info.dark,
                                        fontStyle: 'italic',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5
                                      }}>
                                        📝 {item.observation}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                                <Typography variant="h6" fontWeight="bold" color="primary">
                                  R$ {parseFloat(item.subtotal || 0).toFixed(2)}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>

                  {/* Resumo do Pedido */}
                  <Paper sx={{
                    p: 3,
                    mt: 3,
                    background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                    color: 'white'
                  }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Subtotal
                        </Typography>
                        <Typography variant="h6">
                          R$ {parseFloat(selectedPedido.itens?.reduce((acc, item) => acc + (item.subtotal || 0), 0) || 0).toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Taxa de Entrega
                        </Typography>
                        <Typography variant="h6">
                          R$ {parseFloat(selectedPedido.delivery_fee || 0).toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.3)', my: 1 }} />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          TOTAL DO PEDIDO
                        </Typography>
                        <Typography variant="h4" fontWeight="bold">
                          R$ {parseFloat(selectedPedido.total_amount || 0).toFixed(2)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  {selectedPedido.observation && (
                    <Paper sx={{
                      p: 2,
                      mt: 2,
                      backgroundColor: theme.palette.warning.light,
                      border: `1px solid ${theme.palette.warning.main}`
                    }}>
                      <Typography variant="subtitle2" color="warning.dark" fontWeight="bold" gutterBottom>
                        📝 Observações Gerais do Pedido
                      </Typography>
                      <Typography variant="body2" color="warning.dark">
                        {selectedPedido.observation}
                      </Typography>
                    </Paper>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{
          p: 3,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.grey[50]
        }}>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={() => selectedPedido && handlePrintPedido(selectedPedido)}
            disabled={updating}
            sx={{ mr: 1, borderRadius: 2 }}
          >
            Imprimir Pedido
          </Button>
          <Button
            onClick={() => setDetailDialogOpen(false)}
            variant="outlined"
            size="large"
            sx={{ borderRadius: 2 }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Novo Garçom */}
      <Dialog open={waiterDialogOpen} onClose={() => setWaiterDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: 'secondary.main', color: 'white' }}>Cadastrar Novo Garçom</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Nome"
            margin="normal"
            value={waiterData.name}
            onChange={(e) => setWaiterData({ ...waiterData, name: e.target.value })}
          />
          <TextField
            fullWidth
            label="Email"
            margin="normal"
            value={waiterData.email}
            onChange={(e) => setWaiterData({ ...waiterData, email: e.target.value })}
          />
          <TextField
            fullWidth
            label="Senha"
            type="password"
            margin="normal"
            value={waiterData.password}
            onChange={(e) => setWaiterData({ ...waiterData, password: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaiterDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="secondary" onClick={handleCreateWaiter}>Criar Garçom</Button>
        </DialogActions>
      </Dialog>

      <SubscriptionPaymentDialog
        open={subscriptionPaymentOpen}
        onClose={() => setSubscriptionPaymentOpen(false)}
        lanchoneteId={lanchonete?.id}
        lanchoneteName={lanchonete?.name}
        onSuccess={handleSubscriptionPaymentSuccess}
      />
    </Box>
  );
}

export default AdminLanchonete;
